<script setup lang="ts">
import { Form, ErrorMessage, Field } from 'vee-validate'
import { ALERT_SAFE_MESSAGES } from '~/composables/useKaleidoscopeAlerts'

const { schema, onAddEmailAlerts } = useKaleidoscopeAlerts()
const submitting = ref<boolean>(false)
const status = ref<'idle' | 'success' | 'error'>('idle')
const message = ref<string>('')

const dismissNotice = () => {
  status.value = 'idle'
  message.value = ''
}

const handleSubmit = async (...args: Parameters<typeof onAddEmailAlerts>) => {
  if (submitting.value) return
  submitting.value = true
  status.value = 'idle'
  message.value = ''

  try {
    await onAddEmailAlerts(...args)
    status.value = 'success'
    message.value = 'Subscribed.'
  }
  catch (ex: unknown) {
    status.value = 'error'
    message.value = ex instanceof Error && ALERT_SAFE_MESSAGES.has(ex.message)
      ? ex.message
      : 'Something went wrong. Please try again.'
  }
  finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    id="footer-subscribe"
    class="footer__subscribe"
  >
    <h2 class="footer__subscribe-title">
      Sign up for email alerts
    </h2>
    <div
      v-if="status !== 'idle'"
      :class="['footer__subscribe-notice', `footer__subscribe-notice--${status}`]"
      role="status"
      aria-live="polite"
    >
      <p>{{ message }}</p>
      <button
        type="button"
        class="footer__subscribe-notice-close"
        aria-label="Dismiss"
        @click="dismissNotice"
      >
        ×
      </button>
    </div>
    <Form
      :validation-schema="schema"
      class="footer__subscribe-form"
      @submit="handleSubmit"
    >
      <div class="footer__subscribe-form-group">
        <label
          for="footerEmail"
          class="sr-only"
        >Enter your email</label>
        <div class="footer__subscribe-form-input">
          <Field
            id="footerEmail"
            name="email"
            type="email"
            placeholder="ir@example.com"
          />
          <ErrorMessage
            name="email"
            class="footer__subscribe-error"
          />
        </div>
        <button
          type="submit"
          class="footer__subscribe-form-submit"
          :disabled="submitting"
        >
          <span class="sr-only">Subscribe</span>
          <Icon
            name="bi:arrow-right"
            role="presentation"
          />
        </button>
      </div>
      <div class="footer__subscribe-form-checkboxes">
        <div class="footer__subscribe-form-checkbox">
          <Field
            id="footer-topic-prnews"
            name="topic"
            type="checkbox"
            value="prnews"
          />
          <label for="footer-topic-prnews">News</label>
        </div>
        <div class="footer__subscribe-form-checkbox">
          <Field
            id="footer-topic-weekly"
            name="topic"
            type="checkbox"
            value="weekly"
          />
          <label for="footer-topic-weekly">Events & Presentations</label>
        </div>
        <div class="footer__subscribe-form-checkbox">
          <Field
            id="footer-topic-stock"
            name="topic"
            type="checkbox"
            value="stock"
          />
          <label for="footer-topic-stock">End of Day Stock Quote</label>
        </div>
        <div class="footer__subscribe-form-checkbox">
          <Field
            id="footer-topic-annual"
            name="topic"
            type="checkbox"
            value="sec"
          />
          <label for="footer-topic-annual">SEC Filings</label>
        </div>
      </div>
      <ErrorMessage
        name="topic"
        class="footer__subscribe-error"
      />
    </Form>
  </div>
</template>

<style lang="scss" scoped>
.footer__subscribe {
  @apply w-full;
  @apply lg:w-1/2;

  &-title {
    @apply text-[22px] text-light-text font-semibold;
    @apply md:text-[28px];
  }

  &-form-group {
    @apply w-full border-b border-b-white mb-[30px] flex items-center;
    @apply md:mb-[60px];

    input {
      @apply w-full h-[60px] text-[20px] text-light-text bg-transparent;
      @apply md:h-[90px] md:text-[28px];
    }
  }

  &-form-input {
    @apply grow flex flex-col;

    .footer__subscribe-error {
      @apply mb-[10px];
    }
  }

  &-form-submit {
    @apply border-0 text-[28px] text-light-text pl-[15px] transition-transform duration-300;
    @apply hover:scale-110;
  }

  &-form-checkboxes {
    @apply flex flex-wrap;
  }

  &-form-checkbox {
    @apply min-w-[25%] mr-[20px] mb-[20px] relative z-[1];

    label {
      @apply text-[14px] text-light-text opacity-70 transition-opacity duration-300 flex items-center cursor-pointer;
      @apply hover:opacity-100;
      @apply before:content-[''] before:w-[20px] before:h-[20px] before:border before:border-white before:mr-[8px] before:block before:transition-colors before:duration-300;
    }

    input {
      @apply w-[20px] h-[20px] absolute left-0 top-0 opacity-0;

      &:checked + label {
        @apply before:border-white before:bg-white;
      }

      &:focus + label {
        @apply opacity-100 before:ring-2 before:ring-white;
      }
    }
  }

  &-error {
    @apply text-red-400;
  }

  &-notice {
    @apply mb-[20px] px-[15px] py-[10px] flex items-start justify-between gap-[10px] rounded;
    @apply md:px-[20px] md:py-[15px];

    p {
      @apply text-[14px] leading-[1.4] m-0;
    }

    &--success {
      @apply bg-green-100 text-green-800 border border-green-300;
    }

    &--error {
      @apply bg-red-100 text-red-800 border border-red-300;
    }

    &-close {
      @apply text-[20px] leading-none cursor-pointer opacity-70 hover:opacity-100;
    }
  }
}
</style>
